import { Ship, Compass, ShieldCheck, Handshake, AlertTriangle, Route } from "lucide-react";

const img = (id, w = 1600) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;

export const SERVICES = [
  {
    slug: "fleet-advisory",
    icon: Ship,
    title: "Fleet Advisory",
    category: "Fleet & Assets",
    excerpt:
      "Newbuild strategy, sale & purchase guidance, performance benchmarking and lifecycle optimisation for Owners and Investors.",
    tagline:
      "Build, buy and operate the right fleet, with confidence at every decision point.",
    heroImage: img(14810111),
    overviewHeading: "Your fleet is your largest commitment. We help you get it right.",
    overview: [
      "Your fleet is the single largest capital commitment on your balance sheet. Echo Bravo's Fleet Advisory practice helps Owners, Operators and Investors make those commitments with clarity, from the first feasibility study to the final disposal of a vessel.",
      "We combine commercial modelling, independent technical due diligence and decades of sea-going judgement to ensure every asset earns its place in your fleet and delivers the returns your stakeholders expect.",
    ],
    capabilities: [
      { title: "Newbuilding strategy & supervision", text: "Specification development, yard selection, contract review and on-site construction oversight." },
      { title: "Sale & purchase (S&P) support", text: "Independent valuation, pre-purchase inspections and end-to-end transaction due diligence." },
      { title: "Performance benchmarking", text: "Vessel-by-vessel KPIs measured against peer tonnage to expose efficiency and earnings gaps." },
      { title: "Lifecycle & asset optimisation", text: "Maintenance strategy, lay-up planning and end-of-life or recycling guidance." },
      { title: "Fleet renewal planning", text: "Phasing, financing alignment and decarbonisation-ready tonnage selection." },
    ],
    process: [
      { title: "Discovery & data review", text: "We map your fleet, commercial objectives and constraints." },
      { title: "Analysis & modelling", text: "Technical and commercial assessment with peer benchmarking." },
      { title: "Recommendation", text: "A prioritised, costed action plan you can defend to your board." },
      { title: "Execution support", text: "Hands-on supervision through to delivery or transaction close." },
    ],
    outcomes: [
      "Capital deployed against the highest-return assets",
      "Reduced acquisition risk through independent due diligence",
      "Measurable, benchmarked fleet performance",
      "A renewal roadmap aligned to regulation and financing",
    ],
    stat: { value: "120+", label: "Vessels advised" },
  },
  {
    slug: "marine-operations-consulting",
    icon: Compass,
    title: "Marine Operations Consulting",
    category: "Operations",
    excerpt:
      "End-to-end operational support across crewing, voyage efficiency, bunkering and port performance.",
    tagline:
      "Keep every vessel safe, efficient and earning, voyage after voyage.",
    heroImage: img(753331),
    overviewHeading: "Where margins are won or lost, every single day.",
    overview: [
      "Operational performance is where margins are won or lost. Echo Bravo helps Operators turn day-to-day vessel management into a disciplined, measurable system that performs predictably under pressure.",
      "From crewing to bunkering, we close the gap between the bridge and the boardroom, so your operations run safely, efficiently and profitably across every trade lane.",
    ],
    capabilities: [
      { title: "Crewing & manning strategy", text: "Resourcing, competence frameworks and retention to keep vessels safely manned." },
      { title: "Voyage & passage optimisation", text: "Routeing, speed and weather management to cut both time and fuel." },
      { title: "Bunker management", text: "Procurement strategy, quality assurance and consumption control." },
      { title: "Port & terminal performance", text: "Turnaround analysis, agency oversight and demurrage reduction." },
      { title: "Ship-management oversight", text: "Independent review of third-party managers against your own KPIs." },
    ],
    process: [
      { title: "Discovery", text: "We baseline your current operational performance and pain points." },
      { title: "Operational audit", text: "Deep review of crewing, voyages, bunkers and port calls." },
      { title: "Optimisation plan", text: "A practical roadmap with prioritised, quantified improvements." },
      { title: "Embedded support", text: "We work alongside your team to implement and sustain the gains." },
    ],
    outcomes: [
      "Lower fuel burn and emissions per voyage",
      "Fewer delays and reduced demurrage exposure",
      "Safer, better-resourced crewing",
      "Clear accountability from third-party managers",
    ],
    stat: { value: "24/7", label: "Operations desk" },
  },
  {
    slug: "regulatory-compliance-technical-advisory",
    icon: ShieldCheck,
    title: "Regulatory Compliance & Technical Advisory",
    category: "Compliance & Technical",
    excerpt:
      "IMO, MARPOL, SOLAS and MLC alignment, drydock planning, retrofit and decarbonisation engineering with audit-ready documentation.",
    tagline:
      "Stay ahead of every flag, class and IMO deadline, backed by sound engineering.",
    heroImage: img(12514724),
    overviewHeading: "Regulation is relentless. We keep you audit-ready and technically sound.",
    overview: [
      "Maritime regulation is relentless and unforgiving. A single non-conformity can detain a vessel, void insurance cover or stall a sale. Echo Bravo keeps your fleet audit-ready, every day of the year, translating IMO, flag-state and class requirements into clear procedures your crews and shore teams can actually follow.",
      "Behind every compliant vessel is sound engineering. Our technical advisors bring hands-on experience to drydocks, retrofits and condition assessments, ensuring your technical budgets are spent where they matter most and your fleet is prepared for a lower-carbon future.",
    ],
    capabilities: [
      { title: "IMO & convention compliance", text: "MARPOL, SOLAS, MLC, BWM and ISM / ISPS alignment across the fleet." },
      { title: "Emissions & CII strategy", text: "EEXI, CII rating management and decarbonisation reporting." },
      { title: "Audit & inspection readiness", text: "Port State Control preparation, internal audits and corrective-action tracking." },
      { title: "Class & flag-state liaison", text: "Survey planning, certification and documentation management." },
      { title: "Drydock planning & supervision", text: "Scope definition, yard selection, cost control and on-site oversight." },
      { title: "Retrofit & decarbonisation engineering", text: "Scrubbers, ballast-water systems, energy-saving devices and alternative-fuel readiness." },
      { title: "Condition & pre-purchase assessment", text: "Independent surveys and remaining-life analysis of hull and machinery." },
    ],
    process: [
      { title: "Gap analysis & assessment", text: "We benchmark compliance and vessel condition against every applicable requirement." },
      { title: "Remediation & engineering plan", text: "A prioritised, costed plan to close gaps before they become findings." },
      { title: "Documentation & execution", text: "Procedures, crew training and on-site supervision that make the plan stick." },
      { title: "Ongoing assurance", text: "Continuous monitoring, pre-inspection readiness checks and close-out documentation." },
    ],
    outcomes: [
      "Zero detentions and smoother Port State Control inspections",
      "Confident, audit-ready documentation at all times",
      "Shorter, better-controlled drydock periods",
      "A fleet ready for future fuels, CII and emissions targets",
    ],
    stat: { value: "100%", label: "Compliance success" },
  },
  {
    slug: "commercial-advisory-brokering",
    icon: Handshake,
    title: "Commercial Advisory and Brokering Services",
    category: "Commercial",
    excerpt:
      "Chartering support, sale & purchase brokering, freight negotiation and market intelligence that maximise every fixture.",
    tagline:
      "Commercial outcomes negotiated with an owner's eye and a broker's edge.",
    heroImage: img(1554646),
    overviewHeading: "Every fixture is a negotiation. We make sure you win it.",
    overview: [
      "Freight markets move fast and information is everything. Echo Bravo's commercial team combines broking experience with independent advisory, so every charter, sale or purchase is negotiated from a position of strength.",
      "From single-voyage fixtures to fleet-wide chartering strategy, we act solely in your interest, bringing market intelligence, counterparty insight and disciplined negotiation to every transaction.",
    ],
    capabilities: [
      { title: "Chartering & fixture support", text: "Voyage, time and bareboat charter negotiation with full counterparty vetting." },
      { title: "Sale & purchase brokering", text: "Discreet marketing, buyer and seller representation and transaction management." },
      { title: "Freight & contract negotiation", text: "COAs, charter parties and recap reviews that protect your commercial position." },
      { title: "Market intelligence & analysis", text: "Rate forecasting, tonnage supply tracking and timing guidance for the market cycle." },
      { title: "Post-fixture management", text: "Laytime, demurrage and claims support through to final settlement." },
    ],
    process: [
      { title: "Requirement brief", text: "We define your commercial objectives, constraints and risk appetite." },
      { title: "Market screening", text: "Counterparty search, vetting and shortlisting against live market data." },
      { title: "Negotiation & fixture", text: "We negotiate terms and manage the deal through to a clean fixture or closing." },
      { title: "Post-fixture support", text: "Performance monitoring, claims handling and settlement follow-through." },
    ],
    outcomes: [
      "Stronger rates and terms on every fixture",
      "Reduced counterparty and contract risk",
      "Transactions executed discreetly and efficiently",
      "Market timing informed by independent intelligence",
    ],
    stat: { value: "50+", label: "Successful transactions" },
  },
  {
    slug: "risk-assessment-management",
    icon: AlertTriangle,
    title: "Risk Assessment & Management",
    category: "Risk & Safety",
    excerpt:
      "Marine risk profiling, HSSEQ frameworks, incident investigation and insurance advisory.",
    tagline:
      "See the risk before it finds you.",
    heroImage: img(6572432),
    overviewHeading: "Identify, quantify and control risk before it becomes loss.",
    overview: [
      "Risk in shipping is multi-dimensional: safety, environmental, commercial and reputational. Echo Bravo helps you identify, quantify and control it before it becomes loss.",
      "We build practical risk frameworks that satisfy insurers and regulators while protecting your people, assets and cargo, turning risk management into a genuine operational advantage.",
    ],
    capabilities: [
      { title: "Marine risk profiling", text: "Fleet-wide hazard identification, risk ranking and mitigation planning." },
      { title: "HSSEQ frameworks", text: "Management systems that embed safety into daily operations." },
      { title: "Incident investigation", text: "Independent root-cause analysis and lessons-learned reporting." },
      { title: "Insurance & claims advisory", text: "P&I and H&M support, claims preparation and dispute mitigation." },
      { title: "Emergency response planning", text: "Drills, contingency plans and crisis-readiness programmes." },
    ],
    process: [
      { title: "Risk assessment", text: "We map and quantify the risks across your fleet and operations." },
      { title: "Framework design", text: "Tailored HSSEQ systems and controls fit for your trade." },
      { title: "Implementation & training", text: "We embed the framework and train your teams to own it." },
      { title: "Monitoring", text: "Ongoing review, audits and continuous improvement." },
    ],
    outcomes: [
      "Fewer incidents and lower loss exposure",
      "Stronger relationships with insurers and underwriters",
      "Faster, better-prepared claims handling",
      "Resilient operations and crisis readiness",
    ],
    stat: { value: "360°", label: "Risk coverage" },
  },
  {
    slug: "maritime-strategy-consulting",
    icon: Route,
    title: "Maritime Strategy Consulting",
    category: "Strategy",
    excerpt:
      "Market entry, commercial structuring and long-term fleet strategy for sustainable, profitable growth.",
    tagline:
      "Chart a profitable course through a changing industry.",
    heroImage: img(2144905),
    overviewHeading: "Turn industry disruption into lasting advantage.",
    overview: [
      "Shipping is being reshaped by decarbonisation, geopolitics and digitalisation. Echo Bravo helps Owners and Investors turn that disruption into advantage with clear, independent strategy.",
      "From market entry to long-term fleet strategy, we provide the perspective boards rely on for high-stakes decisions, backed by rigorous analysis and deep sector knowledge.",
    ],
    capabilities: [
      { title: "Market entry & positioning", text: "Segment analysis, opportunity sizing and go-to-market planning." },
      { title: "Commercial structuring", text: "Pool, charter and joint-venture structuring for resilient revenue." },
      { title: "Investment & due diligence", text: "Asset-play analysis and transaction advisory for Investors." },
      { title: "Decarbonisation strategy", text: "Fuel-pathway planning and credible transition roadmaps." },
      { title: "Long-term fleet strategy", text: "Renewal, financing alignment and scenario planning." },
    ],
    process: [
      { title: "Insight", text: "We analyse the market, your position and the forces shaping it." },
      { title: "Strategy", text: "Clear strategic options mapped to your risk appetite." },
      { title: "Business case", text: "A board-ready case with financials and trade-offs." },
      { title: "Execution roadmap", text: "A staged plan to move from decision to delivery." },
    ],
    outcomes: [
      "Confident, evidence-based capital allocation",
      "A resilient, future-proofed business model",
      "Board-ready strategy and investment cases",
      "A credible response to decarbonisation and market shifts",
    ],
    stat: { value: "25+", label: "Years of maritime insight" },
  },
];

const SLUG_ALIASES = {
  "regulatory-compliance": "regulatory-compliance-technical-advisory",
  "technical-advisory": "regulatory-compliance-technical-advisory",
};

export const getServiceBySlug = (slug) =>
  SERVICES.find((s) => s.slug === (SLUG_ALIASES[slug] || slug));
